/**
 * Media-service presign client — ported from student-dashboard-v2 so URL handling
 * is identical to the student website. The only adaptation is the auth token
 * source: the admin dashboard stores its bearer token in localStorage under
 * `pikado_auth_token` (see src/index.tsx InitWithPikadoClient), instead of the
 * student app's Keycloak helper.
 */
import {
  environmentURL,
  getToken,
  type Environment,
} from '@PikaDoApp/npm-pikado-logic/dist/utils/apolloClient';
import {getEnvironment} from '../../lib/getEnvironment';

type BucketInput = 'uploads' | 'assets' | string;

export type PresignedReadUrlResponse = {
  key: string;
  bucket: string;
  url: string;
  expiresIn: number;
};

export type PresignedReadUrlInput = {
  key: string;
  bucket?: BucketInput;
  validateObjectExistence?: boolean;
};

export const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem('pikado_auth_token');
};

export const getUserServiceBaseUrl = (): string => {
  const env = getEnvironment() as Environment;
  const graphqlUrl = environmentURL[env].user;
  return graphqlUrl.replace(/\/graphql\/?$/, '').replace(/\/+$/, '');
};

const normalizeBucket = (bucket?: BucketInput): BucketInput | undefined => {
  if (!bucket) return undefined;
  if (bucket.includes('assets.')) return 'assets';
  if (bucket.includes('uploads.')) return 'uploads';
  return bucket;
};

async function userServiceMediaRequest<T>(
  path: string,
  body: unknown,
  token?: string | null,
): Promise<T> {
  const authToken = token || getAuthToken();
  if (!authToken) {
    throw new Error('Missing auth token. Please sign in again.');
  }

  const env = getEnvironment() as Environment;
  const serviceToken = getToken(env);

  const response = await fetch(`${getUserServiceBaseUrl()}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(serviceToken ? {'X-Auth-Token': serviceToken} : {}),
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(body),
  });

  const text = await response.text();
  const data = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new Error(
      data?.message || `UserService media request failed (${response.status})`,
    );
  }

  return data as T;
}

export async function getPresignedReadUrl(
  input: PresignedReadUrlInput,
  token?: string | null,
): Promise<PresignedReadUrlResponse> {
  return userServiceMediaRequest<PresignedReadUrlResponse>(
    '/media/s3/read-url',
    {...input, bucket: normalizeBucket(input.bucket)},
    token,
  );
}

export async function getPresignedReadUrls(
  items: PresignedReadUrlInput[],
  token?: string | null,
): Promise<PresignedReadUrlResponse[]> {
  return userServiceMediaRequest<PresignedReadUrlResponse[]>(
    '/media/s3/read-urls',
    {
      items: items.map(item => ({
        ...item,
        bucket: normalizeBucket(item.bucket),
      })),
    },
    token,
  );
}
